import React, { useEffect, useMemo, useState } from 'react';
import { ShieldBan, Search, Trash2, X, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Calendar, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface BlacklistEntry {
  id: string;
  cpf: string;
  client_name: string;
  reason: string;
  deleted_from_unit_id: string | null;
  deleted_by_user_id: string | null;
  created_at: string;
  deleted_by: { display_name: string | null; email: string } | null;
}

const formatCPF = (cpf: string) => {
  const n = cpf.replace(/\D/g, '');
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function Blacklist() {
  const { user } = useAuth();
  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [confirmEntry, setConfirmEntry] = useState<BlacklistEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (user) fetchEntries();
  }, [user]);

  const fetchEntries = async () => {
    if (!user?.unit_id) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('cpf_blacklist')
        .select(`
          id, cpf, client_name, reason, deleted_from_unit_id, deleted_by_user_id, created_at,
          deleted_by:users!cpf_blacklist_deleted_by_user_id_fkey(display_name, email)
        `)
        .eq('deleted_from_unit_id', user.unit_id)
        .order('created_at', { ascending: false });
      if (err) throw err;
      setEntries((data || []) as unknown as BlacklistEntry[]);
    } catch (e) {
      console.error('Erro ao carregar blacklist:', e);
      setError('Erro ao carregar a blacklist da unidade.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmEntry) return;
    setDeleting(true);
    try {
      const { error: err } = await supabase
        .from('cpf_blacklist')
        .delete()
        .eq('id', confirmEntry.id);
      if (err) throw err;
      setEntries(prev => prev.filter(e => e.id !== confirmEntry.id));
      setSuccessMessage(`${confirmEntry.client_name} foi removido da blacklist.`);
      setConfirmEntry(null);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (e) {
      console.error('Erro ao remover da blacklist:', e);
      const message = e instanceof Error ? e.message : 'Erro ao remover da blacklist';
      setError(`Erro ao remover da blacklist: ${message}`);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return entries;
    const termDigits = term.replace(/\D/g, '');
    return entries.filter(e => {
      const nameMatch = e.client_name.toLowerCase().includes(term);
      const reasonMatch = e.reason.toLowerCase().includes(term);
      const cpfMatch = termDigits.length > 0 && e.cpf.includes(termDigits);
      return nameMatch || reasonMatch || cpfMatch;
    });
  }, [entries, search]);

  if (user?.role !== 'manager' && user?.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 p-4">
        <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
          <AlertTriangle className="w-7 h-7 text-red-500" />
        </div>
        <p className="text-gray-700 font-semibold">Acesso restrito</p>
        <p className="text-sm text-gray-500">Somente gestores podem gerenciar a blacklist.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <ShieldBan className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900">Blacklist da Unidade</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                CPFs bloqueados nesta unidade. Voce pode remover clientes que voltaram a ser confiaveis.
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-xl flex-shrink-0">
              <ShieldBan className="w-4 h-4 text-red-600" />
              <span className="text-sm font-semibold text-red-700">
                {entries.length} {entries.length === 1 ? 'registro' : 'registros'}
              </span>
            </div>
          </div>
        </div>

        {/* Success banner */}
        {successMessage && (
          <div className="flex items-start gap-3 p-4 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-800">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium">{successMessage}</p>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-3 p-4 rounded-2xl border border-red-200 bg-red-50 text-red-800">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, CPF ou motivo..."
            className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-gray-100 py-16 flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-400">Carregando blacklist...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-gray-700 font-semibold">
              {entries.length === 0 ? 'Nenhum CPF na blacklist' : 'Nenhum resultado para a busca'}
            </p>
            <p className="text-sm text-gray-400 max-w-sm">
              {entries.length === 0
                ? 'Sua unidade nao possui CPFs bloqueados no momento.'
                : 'Tente ajustar os termos da busca.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(entry => (
              <div
                key={entry.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <div className="flex items-stretch">
                  <div className="w-1 bg-red-400 flex-shrink-0" />
                  <div className="flex-1 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-base font-bold text-gray-900 truncate">{entry.client_name}</p>
                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                          {formatCPF(entry.cpf)}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-700">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">
                          Motivo:
                        </span>
                        <span className="break-words">{entry.reason}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(entry.created_at)}
                        </span>
                        {entry.deleted_by && (
                          <span className="inline-flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Registrado por {entry.deleted_by.display_name || entry.deleted_by.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setConfirmEntry(entry)}
                      className="flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-red-50 text-red-700 border border-red-200 text-sm font-semibold hover:bg-red-100 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remover da blacklist
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {confirmEntry && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-900">Confirmar Remocao</h3>
              <button
                onClick={() => setConfirmEntry(null)}
                disabled={deleting}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    O CPF sera liberado para novos cadastros nesta unidade.
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                    O cliente podera ser cadastrado novamente. Certifique-se de que essa e a decisao correta.
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 space-y-1.5">
                <p className="text-sm">
                  <span className="text-gray-500">Cliente: </span>
                  <span className="font-semibold text-gray-900">{confirmEntry.client_name}</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-500">CPF: </span>
                  <span className="font-mono text-gray-900">{formatCPF(confirmEntry.cpf)}</span>
                </p>
                <p className="text-sm">
                  <span className="text-gray-500">Motivo original: </span>
                  <span className="text-gray-800">{confirmEntry.reason}</span>
                </p>
              </div>
              <div className="flex gap-2.5">
                <button
                  onClick={() => setConfirmEntry(null)}
                  disabled={deleting}
                  className="flex-1 h-11 border border-gray-200 text-gray-700 font-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 h-11 flex items-center justify-center gap-2 bg-red-600 text-white font-semibold text-sm rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Removendo...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Remover
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
